import { useEffect } from 'react';

import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import {
  useAddWhiteListMutation,
  useDeleteWhiteListMutation,
  useLazyFetchWhiteListsQuery,
  useUpdateWhiteListMutation,
} from '../api/white-list.api';
import { EecsWhiteListAC, setSelectedWhiteList, setWhiteListModal } from '../store/white-list.slice';
import {
  EecsAddWhiteListPayload,
  EecsDeleteWhiteListPayload,
  EecsUpdateWhiteListPayload,
  IEecsWhiteList,
  IEecsWhiteListModal,
} from '../types';

interface OpenModal {
  modal: IEecsWhiteListModal;
  whiteList?: IEecsWhiteList;
}

interface UseWhiteList {
  fetchWhiteListsHandler: () => Promise<boolean>;
  addWhiteListHandler: (payload: EecsAddWhiteListPayload) => Promise<boolean>;
  updateWhiteListHandler: (payload: EecsUpdateWhiteListPayload) => Promise<boolean>;
  deleteWhiteListHandler: (payload: EecsDeleteWhiteListPayload) => Promise<boolean>;
  openEecsWhiteListModal: ({ modal, whiteList }: OpenModal) => void;
  closeEecsWhiteListModal: (refetch?: boolean) => void;
}

export const useEecsWhiteList = (): UseWhiteList => {
  const dispatch = useAppDispatch();

  const [fetchWhiteListsQuery] = useLazyFetchWhiteListsQuery();
  const [addWhiteListQuery] = useAddWhiteListMutation();
  const [updateWhiteListQuery] = useUpdateWhiteListMutation();
  const [deleteWhiteListQuery] = useDeleteWhiteListMutation();

  const fetchWhiteListsHandler = async () => {
    dispatch(EecsWhiteListAC.toggleLoading(true));
    const { data: response, error } = await fetchWhiteListsQuery({});
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get Eecs Whitelist!');
    } else {
      dispatch(EecsWhiteListAC.setItems(response.data));
    }

    dispatch(EecsWhiteListAC.toggleLoading(false));
    return !isError;
  };

  const addWhiteListHandler = async (payload: EecsAddWhiteListPayload) => {
    const { data: response, error } = await addWhiteListQuery(payload);
    const isError = error || !response?.data;

    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed add Company to whitelist!');
    } else {
      toast.success('Partner company successfully added!');
      await fetchWhiteListsHandler();
    }
    return !isError;
  };

  const updateWhiteListHandler = async (payload: EecsUpdateWhiteListPayload) => {
    dispatch(EecsWhiteListAC.toggleLoading(true));
    const { data: response, error } = await updateWhiteListQuery(payload);
    dispatch(EecsWhiteListAC.toggleLoading(false));
    const isError = error || !response?.data;

    if (isError || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to update Company!');
    } else {
      toast.success('Partner company successfully updated!');
      dispatch(EecsWhiteListAC.editItem(response.data));
    }
    return !isError;
  };

  const deleteWhiteListHandler = async (payload: EecsDeleteWhiteListPayload) => {
    const { error } = await deleteWhiteListQuery(payload);
    const isError = error;

    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to delete Company!');
    } else {
      await fetchWhiteListsHandler();
    }
    return !isError;
  };

  const openEecsWhiteListModal = ({ modal, whiteList }: OpenModal) => {
    dispatch(setWhiteListModal(modal));
    if (whiteList) dispatch(setSelectedWhiteList(whiteList));
  };

  const closeEecsWhiteListModal = async (refetch = false) => {
    if (refetch) {
      await fetchWhiteListsHandler();
    }
    dispatch(setSelectedWhiteList(null));
    dispatch(setWhiteListModal(null));
  };

  useEffect(() => {
    return () => {
      dispatch(EecsWhiteListAC.clearItems());
    };
  }, []);

  return {
    addWhiteListHandler,
    deleteWhiteListHandler,
    fetchWhiteListsHandler,
    updateWhiteListHandler,
    closeEecsWhiteListModal,
    openEecsWhiteListModal,
  };
};
